import { useEffect, useCallback, useState, useMemo, useRef, useId } from 'react';
import SharedModal from '../../../components/Modal';
import { peekConnectorIntent, clearConnectorIntent, handoffToConnector, getConnectorContext } from '../../../connectorIntent';
import BillingTab from './BillingTab';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { clientActions } from '../../../actions/client';
import { authActions } from '../../../actions/auth';
import { uploadImage } from '../../../actions/imageUpload';
import authSlice from '../../../utils/redux/slice/authSlice';
import AppScreenWrapper from '../../../components/app-shell/AppScreenWrapper';
import Sidebar from '../../../components/app-shell/Sidebar';
import Topbar from '../../../components/app-shell/Topbar';
import ScreenOrbs from '../../../components/app-shell/ScreenOrbs';
import useClientData from '../../../hooks/useClientData';
import { apiErrorText } from '../../../utils/apiError';
import TopupCard from './components/TopupCard';
import {
  PlusIcon, TrashIcon, CopyIcon, SearchIcon, CloseIcon,
  EditIcon, UserIcon, CheckIcon, PowerIcon, HistoryIcon,
} from '../../../components/app-shell/icons';

/* Audit lines get read in disputes, so the exact timestamp must always be one
   hover away. Relative under a week (what people actually reason with), an
   absolute short date beyond it — "23w ago" is unusable. */
const eventTime = (iso, short = false) => {
  const t = new Date(iso).getTime();
  if (isNaN(t)) return '';
  const s = Math.max(0, (Date.now() - t) / 1000);
  const suffix = short ? '' : ' ago';
  if (s < 60) return short ? 'now' : 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m${suffix}`;
  if (s < 86400) return `${Math.floor(s / 3600)}h${suffix}`;
  if (s < 604800) return `${Math.floor(s / 86400)}d${suffix}`;
  const d = new Date(t);
  return d.toLocaleDateString('en', d.getFullYear() === new Date().getFullYear()
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
};
const eventTimeFull = (iso) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString();
};

/* Tri-state, deliberately not a boolean. `is_active = 0` with no `disabled_at`
   predates this feature (or came from an admin path) — calling that "Disabled"
   claims an actor and a history that don't exist. */
const keyState = (k) => (k.is_active ? 'active' : (k.disabled_at ? 'disabled' : 'inactive'));

const KEY_STATE_STYLE = {
  active:   { bg: 'rgba(16,185,129,.1)',  border: 'rgba(16,185,129,.25)', fg: '#10B981',      label: 'Active' },
  disabled: { bg: 'rgba(245,158,11,.1)',  border: 'rgba(245,158,11,.28)', fg: '#F59E0B',      label: 'Disabled' },
  inactive: { bg: 'rgba(255,255,255,.04)', border: 'var(--border)',       fg: 'var(--text-4)', label: 'Inactive' },
};

const KEY_STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Disabled' },
];

const ACTOR_ROLE_LABEL = { owner: 'Owner', parent: 'Parent account', grantee: 'Granted access' };
const ACTOR_AUTH_LABEL = { jwt: 'Dashboard', api_key: 'API key' };

/* ─────────────────────────────────────────────────────────────────
   Tab config
───────────────────────────────────────────────────────────────── */
const TABS = ['Profile', 'API Keys', 'Security', 'Team', 'Usage'];

/* ─────────────────────────────────────────────────────────────────
   Account visual adapter for the shared accessible modal
───────────────────────────────────────────────────────────────── */
/* disableLightDismiss: no Escape/backdrop close — for modals showing
   once-only content (created API key) where a stray keypress is destructive */
function Modal({ open, onClose, title, children, width = 480, disableLightDismiss = false, closeDisabled = false, returnFocusRef }) {
  const headingId = useId();
  const headingRef = useRef(null);
  return (
    <SharedModal
      open={open} onClose={onClose} disableLightDismiss={disableLightDismiss} closeDisabled={closeDisabled}
      initialFocusRef={headingRef} returnFocusRef={returnFocusRef}
      aria-labelledby={headingId} aria-busy={closeDisabled || undefined}
      className="uro-app-screen" width="100%" maxWidth={width} maxHeight="calc(100dvh - 48px)"
      backdropStyle={{
        zIndex: 9999, padding: 24, background: 'rgba(0,0,0,.72)',
        flexDirection: 'row', animation: 'none',
      }}
      panelStyle={{
        position: 'relative', overflowY: 'auto', display: 'block',
        background: 'rgba(12,12,20,.98)', borderRadius: 18, padding: 28,
        boxShadow: '0 32px 80px -20px rgba(0,0,0,.85)', animation: 'none',
      }}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
          <span id={headingId} ref={headingRef} data-modal-heading tabIndex={-1} style={{ fontSize: 16, fontWeight: 700, letterSpacing: '-0.02em' }}>{title}</span>
          <button onClick={onClose} disabled={closeDisabled} aria-label="Close dialog" style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,.4)', cursor: closeDisabled ? 'default' : 'pointer', padding: 4, minWidth: 44, minHeight: 44, margin: '-10px -10px -10px 0', display: 'grid', placeItems: 'center' }}>
            <CloseIcon s={16} />
          </button>
        </div>
        {children}
    </SharedModal>
  );
}

/* Simple toast-style feedback (replaces antd message) */
function useToast() {
  const [toasts, setToasts] = useState([]);
  const nextToastId = useRef(0);
  const show = useCallback((text, type = 'info') => {
    const id = ++nextToastId.current;
    setToasts(prev => [...prev, { id, text, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);
  const success = useCallback(text => show(text, 'success'), [show]);
  const error = useCallback(text => show(text, 'error'), [show]);
  const warning = useCallback(text => show(text, 'warning'), [show]);
  return { toasts, success, error, warning };
}

function ToastContainer({ toasts }) {
  if (!toasts.length) return null;
  const colorMap = { success: '#10B981', error: '#F5576C', warning: '#F59E0B', info: '#A5B4FC' };
  return (
    <div data-modal-announcer role="status" aria-live="polite" style={{ position: 'fixed', top: 20, right: 20, zIndex: 99999, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {toasts.map(t => (
        <div key={t.id} style={{
          padding: '10px 16px', borderRadius: 10, fontSize: 13, fontWeight: 500,
          background: 'rgba(12,12,20,.98)', border: `1px solid ${colorMap[t.type]}44`,
          color: colorMap[t.type], boxShadow: '0 8px 24px rgba(0,0,0,.5)',
          maxWidth: 320, animation: 'slideIn .2s ease',
        }}>
          {t.text}
        </div>
      ))}
    </div>
  );
}

/* Simple field input */
function FieldInput({ label, value, onChange, type = 'text', placeholder, disabled, maxLength }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>{label}</label>
      <input
        id={id}
        type={type} value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder} disabled={disabled} maxLength={maxLength}
        style={{
          width: '100%', height: 38, padding: '0 12px', borderRadius: 9,
          background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
          color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
        }}
      />
    </div>
  );
}

/* Skeleton block */
function Skeleton({ w = '100%', h = 20, r = 8 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: r,
      background: 'rgba(255,255,255,.06)',
      animation: 'shimmer 1.5s infinite',
    }} />
  );
}

/* ─────────────────────────────────────────────────────────────────
   Main component
───────────────────────────────────────────────────────────────── */
function AccountScreen() {
  const { balance: shellBalance, clientName: shellName, clientEmail: shellEmail } = useClientData();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const apiKeysTabRef = useRef(null);
  const keyDescriptionId = useId();
  /** Where a finished key gets handed back to, when a connector sent us here. */
  const handoffRef = useRef(null);
  const keyFormPendingRef = useRef(false);
  const connectorRequestRef = useRef(0);
  const [connectorContext, setConnectorContext] = useState(null);
  const [connectorLoading, setConnectorLoading] = useState(false);
  const [connectorError, setConnectorError] = useState('');

  /* Tab */
  const [activeTab, setActiveTab] = useState('Profile');

  /* Profile */
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [photoError, setPhotoError] = useState(false);

  /* API Keys */
  const [apiKeys, setApiKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [keysError, setKeysError] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);
  const [apiSearch, setApiSearch] = useState('');
  const [apiStatus, setApiStatus] = useState('all');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // key id awaiting confirmation

  /* Create / edit API key modal */
  const [keyForm, setKeyForm] = useState(null); // null | { mode: 'create' } | { mode: 'edit', key } | { mode: 'connector', label }
  // The key someone pastes when they already have one. Never stored, never
  // logged — it goes straight back to the connector that asked for it.
  const [keyFormPaste, setKeyFormPaste] = useState('');
  const [keyFormName, setKeyFormName] = useState('');
  const [keyFormDescription, setKeyFormDescription] = useState('');
  const [keyFormSaving, setKeyFormSaving] = useState(false);
  const [keyFormCreated, setKeyFormCreated] = useState(false);
  const [keyFormStatus, setKeyFormStatus] = useState('');
  // Disable / enable. `togglingKeyId` exists because nothing in this file is
  // optimistic — every mutation ends in a refetch, so without it a click has no
  // response for a round-trip and gets double-fired.
  const [togglingKeyId, setTogglingKeyId] = useState(null);
  const [powerForm, setPowerForm] = useState(null); // null | { key, next: boolean }
  const [powerReason, setPowerReason] = useState('');
  const [historyKey, setHistoryKey] = useState(null);
  const [historyEvents, setHistoryEvents] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyTruncated, setHistoryTruncated] = useState(false);

  /* Edit profile modal */
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editImageUrl, setEditImageUrl] = useState('');
  const [editImagePreview, setEditImagePreview] = useState('');
  const [profileUpdating, setProfileUpdating] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  /* Inline name edit */
  const [nameEditing, setNameEditing] = useState(false);
  const [nameEditValue, setNameEditValue] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const nameInputRef = useRef(null);

  /* Security / password */
  const [pwStep, setPwStep] = useState(1);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [pwChanging, setPwChanging] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  /* Team / invitations */
  const [receivedInvitations, setReceivedInvitations] = useState([]);
  const [invError, setInvError] = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invitationActioning, setInvitationActioning] = useState(null);
  const [subAccounts, setSubAccounts] = useState([]);
  const [subLoading, setSubLoading] = useState(false);

  /* Usage */
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);

  /* Logout confirm */
  const [logoutOpen, setLogoutOpen] = useState(false);

  /* ── Data fetchers ── */
  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError(null);
    try {
      const res = await clientActions.getClientProfile();
      setProfile(res?.data ?? res ?? null);
      setPhotoError(false);
    } catch {
      setProfileError('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const fetchApiKeys = useCallback(async () => {
    setKeysLoading(true);
    setKeysError(null);
    try {
      const res = await clientActions.getApiKeys();
      setApiKeys(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
    } catch {
      setKeysError('Failed to load API keys');
    } finally {
      setKeysLoading(false);
    }
  }, []);

  const fetchInvitations = useCallback(async () => {
    setInvLoading(true);
    setInvError(null);
    try {
      const res = await clientActions.getReceivedInvitations();
      setReceivedInvitations(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
    } catch {
      setInvError('Failed to load invitations');
    } finally {
      setInvLoading(false);
    }
  }, []);

  const fetchSubAccounts = useCallback(async () => {
    setSubLoading(true);
    try {
      const res = await clientActions.getSubAccounts();
      setSubAccounts(Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : []);
    } catch {
      setSubAccounts([]);
    } finally {
      setSubLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    setUsageError(null);
    try {
      const res = await clientActions.getApiUsage('30d');
      setUsage(res?.data ?? res ?? null);
    } catch {
      setUsageError('Failed to load usage data');
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
    fetchApiKeys();
    fetchInvitations();
  }, [fetchProfile, fetchApiKeys, fetchInvitations]);

  useEffect(() => {
    if (activeTab === 'Team') fetchSubAccounts();
  }, [activeTab, fetchSubAccounts]);

  useEffect(() => {
    if (activeTab === 'Usage') fetchUsage();
  }, [activeTab, fetchUsage]);

  /* ── Computed ── */
  const balance = profile?.client_balance;
  const balanceTone = balance == null ? 'var(--text-3)' : Number(balance) > 5 ? '#10B981' : Number(balance) > 1 ? '#F59E0B' : '#F5576C';
  const authProvider = profile?.auth_provider || 'local';
  const canChangePassword = authProvider !== 'google';
  const accountType = profile?.account_type || 'standard';
  const isSubAccount = accountType === 'sub';
  // Effective permission to manage your OWN keys (create / rename / delete).
  // The API resolves it: always true for standard/main, the parent-granted flag
  // for sub-accounts — so don't re-derive it from accountType here.
  // The ?? fallback is the pre-feature rule, kept so that a uro-web deploy
  // landing before uro-api (field absent) doesn't strip the button from every
  // standard and main account.
  //
  // Deny until the profile has actually loaded. `profile` starts null and
  // accountType then defaults to 'standard', so without this a DENIED
  // sub-account is shown Create — and, once the (faster) key fetch resolves,
  // per-row Edit and Delete — until the profile lands.
  const canManageKeys = profile ? (profile.can_manage_api_keys ?? !isSubAccount) : false;
  // Billing tab only for the billed (main/standard) entity; subs roll up.
  const visibleTabs = isSubAccount ? TABS : [TABS[0], 'Billing', ...TABS.slice(1)];
  const isMainAccount = accountType === 'main';
  const totalAmountOwed = isMainAccount ? Number(profile?.amount_owed || 0) + Math.max(0, -(Number(balance || 0))) : 0;
  const initials = (profile?.client_name || shellName || 'U').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2) || 'U';

  const filteredKeys = useMemo(() => {
    const s = apiSearch.toLowerCase();
    return apiKeys.filter(k => {
      const nameMatch = !s
        || String(k.api_key_name || '').toLowerCase().includes(s)
        || String(k.description || '').toLowerCase().includes(s)
        // "which keys did X share with me" / "which did X disable" are real
        // questions once the list contains other people's keys.
        || String(k.owner_name || '').toLowerCase().includes(s)
        || String(k.disabled_by_name || '').toLowerCase().includes(s);
      // Filter on the same tri-state the pill renders, so "Disabled" can't
      // return rows the row itself labels "Inactive".
      const st = keyState(k);
      const statusMatch = apiStatus === 'all' || (apiStatus === 'active' && st === 'active') || (apiStatus === 'inactive' && st !== 'active');
      return nameMatch && statusMatch;
    });
  }, [apiKeys, apiSearch, apiStatus]);

  /* ── Handlers ── */
  const invalidateConnectorRequests = useCallback(() => { ++connectorRequestRef.current; }, []);
  const loadConnectorContext = useCallback(async (configureName = false) => {
    const target = handoffRef.current;
    const request = ++connectorRequestRef.current;
    if (!target) return null;
    setConnectorLoading(true);
    setConnectorError('');
    try {
      const context = await getConnectorContext(target);
      if (request !== connectorRequestRef.current || target !== handoffRef.current) return null;
      const callbackPresent = typeof context?.redirectOrigin === 'string' ||
        (context?.redirectOrigin === null && typeof context.redirectScheme === 'string' && /^[a-z][a-z0-9+.-]*:$/.test(context.redirectScheme));
      if (!context || context.returnTo !== target || typeof context.name !== 'string' ||
          !callbackPresent || typeof context.verified !== 'boolean' ||
          (context.verified && (typeof context.host !== 'string' || !context.host.trim()))) {
        throw new Error('Connection details do not match this request.');
      }
      setConnectorContext(context);
      if (configureName) {
        setKeyFormName(`${context.verified ? context.host : 'Unverified application'} connector`);
        setKeyFormDescription(`Created for ${context.verified ? context.host : 'an unverified application'} via ${context.redirectOrigin ?? context.redirectScheme}.`);
      }
      return context;
    } catch {
      if (request === connectorRequestRef.current && target === handoffRef.current) {
        setConnectorContext(null);
        setConnectorError('Could not verify the connection details. Retry, or start a new connection from your assistant if this request expired.');
      }
      return null;
    } finally {
      if (request === connectorRequestRef.current) setConnectorLoading(false);
    }
  }, []);
  const connectorReady = !!connectorContext && !connectorLoading && !connectorError && connectorContext.returnTo === handoffRef.current;
  const refreshConnectorBeforeAction = async () => {
    const shown = connectorContext;
    const fresh = await loadConnectorContext();
    if (!fresh) { setKeyFormStatus(''); return false; }
    if (!shown || ['name', 'host', 'verified', 'redirectOrigin', 'redirectScheme', 'returnTo'].some(field => shown[field] !== fresh[field])) {
      setKeyFormStatus('Connection details changed. Review the application details, then choose how to connect again.');
      return false;
    }
    return true;
  };
  const resetKeyConnection = () => {
    invalidateConnectorRequests();
    setConnectorContext(null);
    setConnectorLoading(false);
    setConnectorError('');
    clearConnectorIntent();
    handoffRef.current = null;
    setKeyFormPaste('');
    setKeyFormCreated(false);
    setKeyFormStatus('');
  };
  const closeKeyForm = () => {
    if (keyFormPendingRef.current) return;
    resetKeyConnection();
    setKeyForm(null);
  };
  const openCreateKeyModal = () => {
    if (keyFormPendingRef.current) return;
    resetKeyConnection();
    setKeyFormName('');
    setKeyFormDescription('');
    setKeyForm({ mode: 'create' });
  };

  /**
   * Land straight on key creation when arriving from a connector.
   *
   * The MCP consent page and the /mcp connect page both send people here to
   * make a key for Claude or ChatGPT. Dropping them on the Profile tab to go
   * hunting is the step that makes connecting feel hard, so ?new_key=... opens
   * the API Keys tab with the create form already filled in and named for
   * whatever sent them. A key per connector is also the reason "what did
   * Claude spend" is answerable, and why revoking Claude does not revoke
   * everything else.
   */
  useEffect(() => {
    // Captured at boot (see connectorIntent), so it survives the redirect to
    // sign in. Reading the query here only worked for someone already
    // authenticated — everyone else landed on a blank account page wondering
    // what had happened.
    const intent = peekConnectorIntent();
    if (!intent) return;

    if (intent.returnTo) handoffRef.current = intent.returnTo;

    setActiveTab('API Keys');
    setKeyFormName('MCP connector');
    setKeyFormDescription('Created for an MCP connector.');
    setKeyFormPaste('');
    // Two ways out, not one. This used to open the create form alone, so
    // someone who already had a key was made to mint another every time they
    // reconnected — "why always asks me to create new key". A picker of
    // existing keys is not possible: api_key is stored as a bcrypt hash, so
    // the secret exists once, at creation, and nobody can hand it back. Paste
    // or create is the whole set of real options.
    setKeyForm(intent.returnTo ? { mode: 'connector' } : { mode: 'create' });
    if (intent.returnTo) loadConnectorContext(true);
    return invalidateConnectorRequests;
  }, [loadConnectorContext, invalidateConnectorRequests]);

  /**
   * Finish the connection with a key the person already has.
   *
   * Revalidate the application identity before handing the secret back.
   * The connector's /authorize/handoff then checks the key against URO.
   */
  const connectWithPastedKey = async () => {
    if (keyFormPendingRef.current || keyForm?.mode !== 'connector' || !connectorReady) return;
    const key = keyFormPaste.trim();
    if (!key) return;
    if (!handoffRef.current) { toast.error('Nothing to connect to — start again from your client.'); return; }
    keyFormPendingRef.current = true;
    setKeyFormSaving(true);
    setKeyFormStatus('Checking connection…');
    let navigating = false;
    try {
      if (!await refreshConnectorBeforeAction()) return;
      setKeyFormStatus('Connecting…');
      handoffToConnector(handoffRef.current, key);
      navigating = true;
    } catch {
      setKeyFormStatus('Could not open the connector. Try connecting with this key again.');
      toast.error('Could not open the connector. Your key is still here to retry.');
    } finally {
      if (!navigating) {
        keyFormPendingRef.current = false;
        setKeyFormSaving(false);
      }
    }
  };

  const openEditKeyModal = (key) => {
    if (keyFormPendingRef.current) return;
    resetKeyConnection();
    setKeyFormName(key.api_key_name || '');
    setKeyFormDescription(key.description || '');
    setKeyForm({ mode: 'edit', key });
  };

  const handleKeyFormSubmit = async () => {
    if (keyFormPendingRef.current || !keyForm || keyFormCreated) return;
    if (keyForm.mode === 'connector' && !connectorReady) return;
    if (!canManageKeys) { toast.warning('This account cannot create or edit API keys.'); return; }
    const name = keyFormName.trim();
    if (!name) { toast.warning('Enter a name for the API key'); return; }
    const description = keyFormDescription.trim() || null;
    keyFormPendingRef.current = true;
    setKeyFormSaving(true);
    setKeyFormStatus('');
    let navigating = false;
    try {
      if (keyForm.mode === 'connector' && !await refreshConnectorBeforeAction()) return;
      const connectorRequest = connectorRequestRef.current;
      if (keyForm?.mode === 'edit') {
        await clientActions.updateApiKey(keyForm.key.client_api_key_id, { name, description });
        setKeyForm(null);
        fetchApiKeys();
        toast.success('Key updated');
      } else {
        const res = await clientActions.createApiKey(name, description);
        if (keyForm.mode === 'connector' && connectorRequest !== connectorRequestRef.current) return;
        const secret = res?.api_key || res?.data?.api_key;
        const created = typeof secret === 'string' ? secret.trim() : '';
        setKeyFormCreated(true);
        fetchApiKeys();
        if (!created) {
          setKeyFormStatus('The server accepted the request but did not return its secret. Check your API keys before creating another.');
          return;
        }

        // Arrived from a connector that told us where to send the key back:
        // finish the connection instead of showing a key to copy. This is the
        // whole difference between "one click" and "open a tab, create a key,
        // select it, copy it, find the other tab, paste it".
        if (keyForm.mode === 'connector' && handoffRef.current) {
          // The secret stays in this mounted form only. A failed navigation
          // can retry the same key without creating another credential.
          setKeyFormPaste(created);
          setKeyFormStatus('Connecting…');
          try {
            handoffToConnector(handoffRef.current, created);
            navigating = true;
          } catch {
            setKeyFormStatus('Your key was created, but the connector could not open. Use “Connect with this key” to retry.');
            toast.error('Could not open the connector. Retry with the key already created.');
          }
          return;
        }

        resetKeyConnection();
        setKeyForm(null);
        setCreatedKey(created);
        setCopied(false);
        setShowKeyModal(true);
        toast.success('API key created');
      }
    } catch (err) {
      toast.error(apiErrorText(err, keyForm?.mode === 'edit' ? 'Failed to update key' : 'Failed to create API key'));
    } finally {
      if (!navigating) {
        keyFormPendingRef.current = false;
        setKeyFormSaving(false);
      }
    }
  };

  /* Disabling always confirms — it needs a sentence of consequence and a reason
     field, neither of which fits the 92px actions cell. Re-enabling a key YOU
     disabled is one click: restoring your own service shouldn't be gated. */
  const requestKeyPower = (key, next) => {
    if (next && key.disabled_by_is_self) {
      applyKeyPower(key, true, '');
      return;
    }
    setPowerReason('');
    setPowerForm({ key, next });
  };

  const applyKeyPower = async (key, next, reason) => {
    setTogglingKeyId(key.client_api_key_id);
    try {
      const res = await clientActions.setApiKeyActive(key.client_api_key_id, next, reason || undefined);
      // The DB is authoritative but a warm auth-cache entry can keep the key
      // working for up to an hour — never show an unqualified success over a
      // credential that might still authenticate.
      if (!next && res && res.cache_invalidated === false) {
        toast.warning('Key disabled, but the auth cache could not be cleared — it may keep working for up to 1 hour.');
      } else {
        toast.success(next
          ? 'Key enabled — requests will be accepted again'
          : 'Key disabled');
      }
    } catch (err) {
      toast.error(apiErrorText(err, next ? 'Failed to enable key' : 'Failed to disable key'));
    } finally {
      setTogglingKeyId(null);
      // Cleared in `finally`, not just on success: keeping the pre-fetch
      // snapshot open after a failure lets the modal's banner contradict the
      // freshly refetched row behind it.
      setPowerForm(null);
      // In `finally`, not the success branch: this is a multi-actor feature, so
      // a failure often means someone else changed the key and the row is stale.
      fetchApiKeys();
    }
  };

  const openKeyHistory = async (key) => {
    setHistoryKey(key);
    setHistoryEvents([]);
    setHistoryError('');
    setHistoryTruncated(false);
    setHistoryLoading(true);
    try {
      const res = await clientActions.getApiKeyEvents(key.client_api_key_id);
      setHistoryEvents(res.events);
      setHistoryTruncated(res.truncated);
    } catch (err) {
      setHistoryError(apiErrorText(err, 'Failed to load key history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRemoveKey = async (id) => {
    try {
      await clientActions.removeApiKey(id);
      fetchApiKeys();
      toast.success('Key removed');
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to remove key'));
    } finally {
      setDeleteConfirm(null);
    }
  };

  const handleCopyKey = () => {
    if (createdKey) {
      navigator.clipboard.writeText(createdKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleOpenEditProfile = () => {
    setEditName(profile?.client_name || '');
    setEditImageUrl(profile?.profile_image_url || '');
    setEditImagePreview(profile?.profile_image_url || '');
    setEditModalOpen(true);
  };

  const handleProfileImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => setEditImagePreview(ev.target.result);
    reader.readAsDataURL(file);
    setImageUploading(true);
    try {
      const result = await uploadImage(file);
      setEditImageUrl(result.image_url || result?.data?.image_url || '');
    } catch {
      toast.error('Failed to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    const name = editName?.trim();
    if (!name || name.length < 2) { toast.warning('Name must be at least 2 characters'); return; }
    setProfileUpdating(true);
    try {
      await clientActions.updateProfile({ client_name: name, profile_image_url: editImageUrl || null });
      toast.success('Profile updated');
      setEditModalOpen(false);
      fetchProfile();
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to update profile'));
    } finally {
      setProfileUpdating(false);
    }
  };

  const handleInlineNameSave = async () => {
    const name = nameEditValue?.trim();
    if (!name || name.length < 2) { toast.warning('Name must be at least 2 characters'); return; }
    setNameSaving(true);
    try {
      await clientActions.updateProfile({ client_name: name });
      toast.success('Name updated');
      setNameEditing(false);
      fetchProfile();
    } catch {
      toast.error('Failed to update name');
    } finally {
      setNameSaving(false);
    }
  };

  const startNameEdit = () => {
    setNameEditValue(profile?.client_name || '');
    setNameEditing(true);
    setTimeout(() => nameInputRef.current?.focus(), 50);
  };

  const handleRequestOtp = async () => {
    if (!oldPassword) { toast.warning('Enter your current password'); return; }
    if (!newPassword || newPassword.length < 8) { toast.warning('New password must be at least 8 characters'); return; }
    if (newPassword !== confirmPassword) { toast.warning('Passwords do not match'); return; }
    setOtpSending(true);
    try {
      await clientActions.requestPasswordChangeOtp();
      toast.success('Verification code sent to your email');
      setPwStep(2);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to send verification code'));
    } finally {
      setOtpSending(false);
    }
  };

  const handleChangePassword = async () => {
    const otpVal = otp?.trim();
    if (!otpVal || otpVal.length !== 6) { toast.warning('Enter the 6-digit verification code'); return; }
    setPwChanging(true);
    try {
      await clientActions.changePassword({ old_password: oldPassword, new_password: newPassword, otp: otpVal });
      toast.success('Password changed successfully');
      setPwSuccess(true);
      setOldPassword(''); setNewPassword(''); setConfirmPassword(''); setOtp('');
      setPwStep(1);
    } catch (err) {
      toast.error(apiErrorText(err, 'Failed to change password'));
    } finally {
      setPwChanging(false);
    }
  };

  const handleAcceptInvitation = async (inv) => {
    setInvitationActioning(inv.invitation_id + '_accept');
    try {
      await clientActions.acceptInvitation({ invitation_id: inv.invitation_id });
      toast.success('Invitation accepted');
      const verifyRes = await authActions.verifyToken();
      if (verifyRes?.user) dispatch(authSlice.actions.setUser(verifyRes.user));
      fetchProfile();
      fetchInvitations();
    } catch (err) {
      toast.error(err?.error || err?.message || 'Failed to accept invitation');
    } finally {
      setInvitationActioning(null);
    }
  };

  const handleRejectInvitation = async (inv) => {
    setInvitationActioning(inv.invitation_id + '_reject');
    try {
      await clientActions.rejectInvitation(inv.invitation_id);
      toast.success('Invitation declined');
      fetchInvitations();
    } catch (err) {
      toast.error(err?.error || err?.message || 'Failed to decline invitation');
    } finally {
      setInvitationActioning(null);
    }
  };

  const handleLogout = () => {
    authActions.performLogout();
    navigate('/login');
  };

  /* ── Eye icon (for password fields) ── */
  const EyeIcon = ({ open }) => (
    <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      {open
        ? <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z"/><circle cx="12" cy="12" r="3"/></>
        : <><path d="M17.9 17.9A10.9 10.9 0 0 1 12 20C5 20 1 12 1 12a21 21 0 0 1 3.1-4.9M6.5 6.5A10.9 10.9 0 0 1 12 4c7 0 11 8 11 8a21 21 0 0 1-2.1 3.4M1 1l22 22"/></>
      }
    </svg>
  );

  /* ── Password field with toggle ── */
  const PwField = ({ label, value, onChange, show, onToggleShow, placeholder }) => (
    <div>
      <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>{label}</div>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'} value={value}
          onChange={e => onChange(e.target.value)} placeholder={placeholder}
          style={{
            width: '100%', height: 38, padding: '0 40px 0 12px', borderRadius: 9,
            background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
            color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit',
          }}
        />
        <button
          type="button" onClick={onToggleShow}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', padding: 4, display: 'grid', placeItems: 'center' }}
        >
          <EyeIcon open={show} />
        </button>
      </div>
    </div>
  );

  /* ══════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════ */
  return (
    <AppScreenWrapper>
      <style>{`
        @keyframes shimmer { 0%,100% { opacity:.5 } 50% { opacity:1 } }
        @keyframes slideIn { from { opacity:0; transform:translateX(12px) } to { opacity:1; transform:none } }
        .acct-key-row:hover { background: rgba(255,255,255,.03) !important; }
        .acct-del-btn:hover { color: var(--red) !important; }
        .acct-edit-btn:hover { color: #fff !important; }
        /* Must come AFTER .acct-key-row:hover — identical specificity, both
           !important, so source order decides which wins on a disabled row. */
        .acct-key-row-off:hover { background: rgba(245,158,11,.06) !important; }
        .acct-power-btn:hover { color: #F59E0B !important; }
        .acct-key-row-off .acct-power-btn:hover { color: #10B981 !important; }
        .acct-hist-btn:hover { color: rgba(255,255,255,.72) !important; border-bottom-color: rgba(255,255,255,.4) !important; }
        .acct-tab:hover { color: rgba(255,255,255,.75) !important; }
      `}</style>

      <ToastContainer toasts={toast.toasts} />

      <div className="screen-root" style={{ display: 'flex' }}>
        <Sidebar balance={shellBalance} name={shellName} email={shellEmail} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, position: 'relative' }}>
          <ScreenOrbs tone="indigo" />
          <Topbar title="Account" name={shellName} />

          <div className="scroll-y app-screen-content" style={{ flex: 1, padding: '28px 32px 48px', position: 'relative', zIndex: 1 }}>

            {/* Pending invitation banners (always visible) */}
            {receivedInvitations.length > 0 && (
              <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {receivedInvitations.map(inv => (
                  <div key={inv.invitation_id} className="row acct-inv-row" style={{
                    gap: 14, padding: '14px 18px', borderRadius: 12,
                    background: 'rgba(99,102,241,.08)', border: '1px solid rgba(99,102,241,.25)',
                  }}>
                    <div style={{
                      width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                      background: 'linear-gradient(135deg, #6366F1, #8B5CF6)',
                      display: 'grid', placeItems: 'center',
                    }}>
                      <UserIcon s={16} />
                    </div>
                    <div style={{ flex: 1, fontSize: 13 }}>
                      <strong>{inv.inviter_name || inv.inviting_client_name || 'Someone'}</strong>
                      {inv.inviter_email ? ` (${inv.inviter_email})` : ''} invited you to be a sub-account.
                    </div>
                    <div className="row" style={{ gap: 8 }}>
                      <button
                        className="btn btn-ghost btn-sm"
                        disabled={!!invitationActioning}
                        onClick={() => handleRejectInvitation(inv)}
                      >
                        {invitationActioning === inv.invitation_id + '_reject' ? 'Declining…' : 'Decline'}
                      </button>
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={!!invitationActioning}
                        onClick={() => handleAcceptInvitation(inv)}
                      >
                        <CheckIcon s={12} />
                        {invitationActioning === inv.invitation_id + '_accept' ? 'Accepting…' : 'Accept'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Sub-account info banner */}
            {isSubAccount && (
              <div style={{
                marginBottom: 20, padding: '12px 16px', borderRadius: 12,
                background: 'rgba(245,158,11,.06)', border: '1px solid rgba(245,158,11,.2)',
                fontSize: 13, color: '#F59E0B',
              }}>
                <strong>Sub-account</strong>
                <span style={{ color: 'var(--text-3)', marginLeft: 6 }}>
                  {canManageKeys
                    ? 'Contact your account admin to top up your balance.'
                    : 'Contact your account admin to top up or manage API keys.'}
                </span>
              </div>
            )}

            {/* Tab pill bar */}
            <div className="row acct-tabs no-scrollbar" style={{ gap: 4, marginBottom: 26, padding: 4, borderRadius: 10, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)', width: 'fit-content' }}>
              {visibleTabs.map(t => (
                <button key={t}
                  ref={t === 'API Keys' ? apiKeysTabRef : undefined}
                  className="acct-tab"
                  onClick={() => setActiveTab(t)}
                  style={{
                    height: 32, padding: '0 14px', borderRadius: 7, cursor: 'pointer',
                    background: activeTab === t ? 'rgba(255,255,255,.08)' : 'transparent',
                    border: activeTab === t ? '1px solid rgba(255,77,141,.35)' : '1px solid transparent',
                    color: activeTab === t ? '#FFC2B5' : 'var(--text-3)',
                    fontSize: 13, fontWeight: activeTab === t ? 600 : 500,
                    transition: 'all .15s',
                  }}
                >{t}</button>
              ))}
            </div>

            {/* ═══════════════════════════════════════════════════
                Tab 1: Profile
            ═══════════════════════════════════════════════════ */}
            {activeTab === 'Profile' && (
              <div className="acct-profile-grid" style={{ display: 'grid', gridTemplateColumns: '1.35fr 1fr', gap: 20 }}>

                {/* Profile card */}
                <div className="card" style={{ padding: 24 }}>
                  {profileLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div className="row" style={{ gap: 16 }}>
                        <Skeleton w={76} h={76} r="50%" />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <Skeleton w="60%" h={20} />
                          <Skeleton w="80%" h={14} />
                        </div>
                      </div>
                      <Skeleton h={1} />
                      <div className="acct-field-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <Skeleton h={38} /><Skeleton h={38} /><Skeleton h={38} /><Skeleton h={38} />
                      </div>
                    </div>
                  ) : profileError ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-3)' }}>
                      <div style={{ fontSize: 14, marginBottom: 10 }}>{profileError}</div>
                      <button className="btn btn-ghost btn-sm" onClick={fetchProfile}>Retry</button>
                    </div>
                  ) : (
                    <>
                      <div className="row" style={{ gap: 18, alignItems: 'flex-start' }}>
                        {/* Avatar with upload click */}
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          title="Change photo"
                          style={{
                            width: 76, height: 76, borderRadius: '50%', flex: '0 0 76px', cursor: 'pointer',
                            background: !profile?.profile_image_url ? 'linear-gradient(135deg, #FF7E67, #FF4D8D)' : undefined,
                            display: 'grid', placeItems: 'center', fontSize: 28, fontWeight: 700,
                            boxShadow: '0 12px 30px -10px rgba(255,77,141,.5)', overflow: 'hidden',
                            position: 'relative',
                          }}
                        >
                          {profile?.profile_image_url && !photoError
                            ? <img src={profile.profile_image_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setPhotoError(true)} />
                            : initials
                          }
                          {imageUploading && (
                            <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.55)', display: 'grid', placeItems: 'center', fontSize: 11, color: '#fff' }}>…</div>
                          )}
                        </div>
                        {!editModalOpen && <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfileImageChange} style={{ display: 'none' }} />}

                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Inline name edit */}
                          {nameEditing ? (
                            <div className="row" style={{ gap: 8 }}>
                              <input
                                ref={nameInputRef}
                                value={nameEditValue}
                                onChange={e => setNameEditValue(e.target.value)}
                                onBlur={handleInlineNameSave}
                                onKeyDown={e => { if (e.key === 'Enter') handleInlineNameSave(); if (e.key === 'Escape') setNameEditing(false); }}
                                style={{
                                  flex: 1, height: 34, padding: '0 10px', borderRadius: 8,
                                  background: 'rgba(255,255,255,.07)', border: '1px solid rgba(255,77,141,.4)',
                                  color: '#fff', fontSize: 18, fontWeight: 700, outline: 'none',
                                  letterSpacing: '-0.02em',
                                }}
                              />
                              {nameSaving && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>Saving…</span>}
                            </div>
                          ) : (
                            <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                              <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>{profile?.client_name || shellName}</span>
                              {authProvider !== 'local' && (
                                <span className="chip" style={{ height: 20, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.04em', color: '#93C5FD', borderColor: 'rgba(59,130,246,.3)', background: 'rgba(59,130,246,.1)' }}>
                                  {authProvider === 'both' ? 'Google+Email' : authProvider}
                                </span>
                              )}
                              {(isMainAccount || isSubAccount) && (
                                <span className="chip" style={{ height: 20, fontSize: 9, textTransform: 'uppercase', letterSpacing: '.04em', color: isMainAccount ? '#A5B4FC' : '#FCD34D', borderColor: isMainAccount ? 'rgba(99,102,241,.3)' : 'rgba(245,158,11,.3)', background: isMainAccount ? 'rgba(99,102,241,.1)' : 'rgba(245,158,11,.1)' }}>
                                  {isMainAccount ? 'Main' : 'Sub'}
                                </span>
                              )}
                              <button onClick={startNameEdit} style={{ background: 'none', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 2, display: 'grid', placeItems: 'center' }} title="Edit name">
                                <EditIcon s={13} />
                              </button>
                            </div>
                          )}

                          <div style={{ fontSize: 13, color: 'var(--text-3)', marginTop: 4 }}>{profile?.client_email || shellEmail}</div>

                          <div className="row" style={{ gap: 8, marginTop: 14 }}>
                            <button className="btn btn-ghost btn-sm" onClick={handleOpenEditProfile}>
                              <EditIcon s={11} /> Edit profile
                            </button>
                            {canChangePassword && (
                              <button className="btn btn-ghost btn-sm" onClick={() => { setActiveTab('Security'); setPwSuccess(false); }}>
                                Change password
                              </button>
                            )}
                          </div>
                        </div>
                      </div>

                      <div style={{ height: 1, background: 'var(--border)', margin: '22px 0' }} />

                      <div className="acct-field-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        {[
                          { l: 'Display name', v: profile?.client_name || '—' },
                          { l: 'Email', v: profile?.client_email || '—' },
                          { l: 'Account type', v: accountType === 'sub' ? 'Sub-account' : accountType === 'main' ? 'Main account' : 'Standard' },
                          { l: 'Auth provider', v: authProvider === 'both' ? 'Google + Email' : authProvider === 'google' ? 'Google' : 'Email' },
                        ].map(f => (
                          <div key={f.l}>
                            <div style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase', marginBottom: 6 }}>{f.l}</div>
                            <div style={{ height: 38, padding: '0 12px', borderRadius: 8, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.v}</div>
                          </div>
                        ))}
                      </div>

                      {/* Budget info for sub-accounts */}
                      {isSubAccount && profile?.budget && (
                        <div className="acct-field-grid" style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {profile.budget.daily_limit != null && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Daily limit</div>
                              <div style={{ height: 38, padding: '0 12px', borderRadius: 8, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', fontSize: 13 }}>
                                {Number(profile.budget.daily_spent || 0).toFixed(2)} / {Number(profile.budget.daily_limit).toFixed(2)} URO
                              </div>
                            </div>
                          )}
                          {profile.budget.monthly_limit != null && (
                            <div>
                              <div style={{ fontSize: 10, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Monthly limit</div>
                              <div style={{ height: 38, padding: '0 12px', borderRadius: 8, background: 'rgba(255,255,255,.03)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', fontSize: 13 }}>
                                {Number(profile.budget.monthly_spent || 0).toFixed(2)} / {Number(profile.budget.monthly_limit).toFixed(2)} URO
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Balance + Topup */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <div className="card" style={{ padding: 22 }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span style={{ fontSize: 11, color: 'var(--text-3)', letterSpacing: '.04em', textTransform: 'uppercase' }}>Balance</span>
                      {balance != null && Number(balance) < 5 && (
                        <span className="chip" style={{ height: 20, fontSize: 10, color: '#FCD34D', background: 'rgba(245,158,11,.1)', borderColor: 'rgba(245,158,11,.3)' }}>
                          Low
                        </span>
                      )}
                    </div>
                    <div className="row" style={{ alignItems: 'baseline', gap: 6, marginTop: 8 }}>
                      <span style={{ fontSize: 40, fontWeight: 800, letterSpacing: '-0.03em', color: balanceTone }}>
                        {profileLoading ? '…' : balance != null ? Number(balance).toFixed(4) : '—'}
                      </span>
                      <span style={{ color: 'var(--text-3)', fontSize: 14 }}>URO</span>
                    </div>
                    <div style={{ height: 6, background: 'rgba(255,255,255,.06)', borderRadius: 3, marginTop: 12, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, Math.max(0, Number(balance || 0) * 10))}%`, height: '100%', background: 'var(--grad)', transition: 'width .4s ease' }} />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 10 }}>
                      ~{balance != null ? Math.floor(Number(balance) / 0.004) : '—'} images or {balance != null ? Math.floor(Number(balance) / 0.08) : '—'}s video
                    </div>
                    {isMainAccount && profile?.sub_accounts_total_balance != null && (
                      <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-3)' }}>
                        Sub-accounts: <span style={{ color: '#A5B4FC', fontWeight: 600 }}>{Number(profile.sub_accounts_total_balance).toFixed(4)} URO</span>
                      </div>
                    )}
                    {totalAmountOwed > 0 && (
                      <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: 'rgba(245,87,108,.08)', border: '1px solid rgba(245,87,108,.2)', fontSize: 12, color: 'var(--red)' }}>
                        Amount owed: <strong>{totalAmountOwed.toFixed(2)} URO</strong>
                      </div>
                    )}
                  </div>

                  {!isSubAccount && (
                    <TopupCard
                      amountOwed={totalAmountOwed}
                      showFees={isMainAccount}
                      canSkipAmountOwed={!!profile?.can_skip_amount_owed}
                    />
                  )}

                  {/* Sign out in profile */}
                  <div className="card" style={{ padding: '14px 18px', borderColor: 'rgba(245,87,108,.2)', background: 'rgba(245,87,108,.04)' }}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--red)' }}>Sign out</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Sign out on this device</div>
                      </div>
                      <button onClick={() => setLogoutOpen(true)} className="btn btn-sm" style={{ background: 'rgba(245,87,108,.1)', border: '1px solid rgba(245,87,108,.35)', color: 'var(--red)' }}>
                        Sign out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ═══════════════════════════════════════════════════
                Tab 2: API Keys
            ═══════════════════════════════════════════════════ */}
            {activeTab === 'Billing' && (
              <BillingTab isSubAccount={isSubAccount} toast={toast} />
            )}

            {activeTab === 'API Keys' && (
              <section>
                <div className="row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
                  <div>
                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>API keys</h2>
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
                      Keys are shown once on creation. Rotate them often.
                    </div>
                  </div>
                  {canManageKeys && (
                    <button className="btn btn-primary btn-sm" onClick={openCreateKeyModal}>
                      <PlusIcon s={12} /> Create key
                    </button>
                  )}
                </div>

                {/* Filters */}
                <div className="row acct-keyfilters" style={{ gap: 8, marginBottom: 14 }}>
                  <div style={{ position: 'relative', flex: 1, maxWidth: 280 }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-4)', pointerEvents: 'none' }}>
                      <SearchIcon s={12} />
                    </span>
                    <input
                      value={apiSearch}
                      onChange={e => setApiSearch(e.target.value)}
                      placeholder="Search keys…"
                      style={{
                        width: '100%', height: 32, paddingLeft: 30, paddingRight: 10,
                        borderRadius: 8, background: 'rgba(255,255,255,.04)',
                        border: '1px solid var(--border)', color: '#fff', fontSize: 12, outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  {/* Label changed, value kept — the 'inactive' filter must still
                      catch legacy keys that have no disabled_at. */}
                  {KEY_STATUS_FILTERS.map(f => (
                    <button key={f.value} onClick={() => setApiStatus(f.value)} style={{
                      height: 32, padding: '0 12px', borderRadius: 8, cursor: 'pointer',
                      border: `1px solid ${apiStatus === f.value ? 'rgba(99,102,241,.4)' : 'var(--border)'}`,
                      background: apiStatus === f.value ? 'rgba(99,102,241,.1)' : 'rgba(255,255,255,.03)',
                      color: apiStatus === f.value ? '#A5B4FC' : 'var(--text-3)',
                      fontSize: 12, fontWeight: 600,
                    }}>{f.label}</button>
                  ))}
                </div>

                {/* Keys table */}
                <div className="card" style={{ overflow: 'hidden' }}>
                  <div className="row acct-keys-head" style={{
                    padding: '10px 18px', borderBottom: '1px solid var(--border)',
                    fontSize: 10, color: 'var(--text-4)', letterSpacing: '.05em', textTransform: 'uppercase',
                    background: 'rgba(255,255,255,.02)',
                  }}>
                    <span className="acct-col-prefix" style={{ flex: '0 0 200px' }}>Key prefix</span>
                    <span style={{ flex: 1 }}>Label</span>
                    <span style={{ flex: '0 0 128px' }}>Status</span>
                    <span className="acct-col-created" style={{ flex: '0 0 120px' }}>Created</span>
                    <span className="acct-col-lastused" style={{ flex: '0 0 110px' }}>Last used</span>
                    <span style={{ flex: '0 0 92px' }} />
                  </div>

                  {keysLoading ? (
                    <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[1, 2, 3].map(i => <Skeleton key={i} h={42} />)}
                    </div>
                  ) : keysError ? (
                    <div style={{ padding: '28px 18px', textAlign: 'center', color: 'var(--text-3)' }}>
                      <div style={{ fontSize: 14, marginBottom: 10 }}>{keysError}</div>
                      <button className="btn btn-ghost btn-sm" onClick={fetchApiKeys}>Retry</button>
                    </div>
                  ) : filteredKeys.length === 0 ? (
                    <div style={{ padding: '32px 18px', textAlign: 'center', color: 'var(--text-4)', fontSize: 13 }}>
                      {apiKeys.length === 0
                        ? (canManageKeys
                            ? 'No API keys yet — create one above'
                            : 'No API keys. Keys your administrator shares with you will appear here.')
                        : 'No keys match your filter'}
                    </div>
                  ) : (
                    filteredKeys.map((k, i) => {
                      const state = keyState(k);
                      const tone = KEY_STATE_STYLE[state];
                      const isOff = state === 'disabled';
                      return (
                      <div key={k.client_api_key_id || i}
                        className={`acct-key-row row${isOff ? ' acct-key-row-off' : ''}`}
                        style={{
                        padding: '13px 18px', gap: 0,
                        borderBottom: i < filteredKeys.length - 1 ? '1px solid var(--border)' : 'none',
                        transition: 'background .15s',
                        // Amber, not dimmed. A deliberately disabled key is an
                        // anomaly someone created on purpose — and it's the row
                        // you came here to act on. Greying it says "skip me".
                        ...(isOff ? { background: 'rgba(245,158,11,.035)', boxShadow: 'inset 2px 0 0 #F59E0B' } : null),
                      }}>
                        <span className="mono acct-col-prefix" style={{ flex: '0 0 200px', fontSize: 12, color: k.api_key_prefix?.includes('live') ? '#FFC2A8' : '#A5B4FC', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {k.api_key_prefix || k.key_preview || '•••'}
                        </span>
                        <div className="acct-col-label" style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {k.api_key_name || '—'}
                            </span>
                            {/* Shared keys are listed here because this is the
                                page with the controls — without them a grantee
                                saw an empty page while Usage showed the keys. */}
                            {k.is_shared && (
                              <span
                                title={`Shared with you by ${k.owner_name || 'another account'}`}
                                style={{
                                  flexShrink: 0, fontSize: 10, fontWeight: 600, padding: '1px 6px',
                                  borderRadius: 5, background: 'rgba(139,92,246,.12)', color: '#A78BFA',
                                  maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}
                              >
                                {k.owner_name || 'Shared'}
                              </span>
                            )}
                          </div>
                          {k.description ? (
                            <div title={k.description} style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {k.description}
                            </div>
                          ) : null}
                        </div>
                        <span className="acct-col-status" style={{ flex: '0 0 128px' }}>
                          <span
                            title={isOff && k.disabled_at
                              ? `Disabled by ${k.disabled_by_is_self ? 'you' : (k.disabled_by_name || 'someone')} on ${eventTimeFull(k.disabled_at)}`
                              : undefined}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                              background: tone.bg,
                              border: `1px solid ${tone.border}`,
                              color: tone.fg,
                            }}>
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: tone.fg }} />
                            {tone.label}
                          </span>
                          {k.usage_limit_per_minute != null && (
                            <span style={{ display: 'block', fontSize: 10, color: 'var(--text-4)', marginTop: 3 }}>{k.usage_limit_per_minute}/min</span>
                          )}
                          {/* Attribution doubles as the entry point to the log —
                              a fourth icon in the row would be icon soup, and
                              history is a read action, not a peer of the writes. */}
                          <button
                            className="acct-hist-btn"
                            onClick={() => openKeyHistory(k)}
                            title={isOff && k.disabled_at
                              ? `Disabled by ${k.disabled_by_is_self ? 'you' : (k.disabled_by_name || 'someone')} on ${eventTimeFull(k.disabled_at)}`
                              : 'View this key’s history'}
                            style={{
                              display: 'block', marginTop: 3, padding: 0, background: 'none', border: 'none',
                              borderBottom: '1px dashed rgba(255,255,255,.18)',
                              fontSize: 10, lineHeight: 1.4, color: 'var(--text-4)', cursor: 'pointer',
                              maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                              fontFamily: 'inherit', textAlign: 'left',
                            }}>
                            {isOff && k.disabled_at
                              ? `by ${k.disabled_by_is_self ? 'you' : ((k.disabled_by_name || 'someone').split(' ')[0])} · ${eventTime(k.disabled_at, true)}`
                              : 'History'}
                          </button>
                        </span>
                        <span className="acct-col-created" style={{ flex: '0 0 120px', fontSize: 12, color: 'var(--text-3)' }}>
                          {k.created_at ? new Date(k.created_at).toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                        </span>
                        <span className="acct-col-lastused" style={{ flex: '0 0 110px', fontSize: 12, color: 'var(--text-3)' }} title={k.last_used_at ? new Date(k.last_used_at).toLocaleString() : 'No recorded use'}>
                          {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString('en', { month: 'short', day: 'numeric', year: '2-digit' }) : '—'}
                        </span>
                        <span className="acct-col-actions" style={{ flex: '0 0 92px', textAlign: 'right' }}>
                          {/* All three share one gate: the permission covers
                              create, rename, delete and disable together. */}
                          {!canManageKeys ? null : deleteConfirm === k.client_api_key_id ? (
                            <div className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                              <button onClick={() => handleRemoveKey(k.client_api_key_id)} style={{ fontSize: 11, color: 'var(--red)', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: '2px 6px' }}>Yes</button>
                              <button onClick={() => setDeleteConfirm(null)} style={{ fontSize: 11, color: 'var(--text-4)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px' }}>No</button>
                            </div>
                          ) : (
                            <div className="row" style={{ gap: 2, justifyContent: 'flex-end' }}>
                              {/* Rests amber when the key is off, so on a disabled
                                  row the "bring it back" control is the one
                                  coloured thing in the cell. */}
                              <button
                                className="acct-power-btn"
                                title={k.is_active ? 'Disable key' : 'Enable key'}
                                aria-label={k.is_active ? 'Disable key' : 'Enable key'}
                                disabled={togglingKeyId === k.client_api_key_id}
                                onClick={() => requestKeyPower(k, !k.is_active)}
                                style={{
                                  background: 'transparent', border: 'none', cursor: 'pointer',
                                  padding: 6, transition: 'color .15s',
                                  color: k.is_active ? 'var(--text-4)' : '#F59E0B',
                                  opacity: togglingKeyId === k.client_api_key_id ? 0.4 : 1,
                                  pointerEvents: togglingKeyId === k.client_api_key_id ? 'none' : undefined,
                                }}
                              >
                                <PowerIcon s={14} />
                              </button>
                              <button
                                className="acct-edit-btn"
                                title="Edit key"
                                onClick={() => openEditKeyModal(k)}
                                style={{ background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 6, transition: 'color .15s' }}
                              >
                                <EditIcon s={14} />
                              </button>
                              {/* Delete is the one irreversible action here —
                                  soft-delete has no undo in the UI — so it stays
                                  with the key's owner. can_delete comes from the
                                  API rather than being re-derived. */}
                              {k.can_delete !== false && (
                                <button
                                  className="acct-del-btn"
                                  title="Delete key"
                                  onClick={() => setDeleteConfirm(k.client_api_key_id)}
                                  style={{ background: 'transparent', border: 'none', color: 'var(--text-4)', cursor: 'pointer', padding: 6, transition: 'color .15s' }}
                                >
                                  <TrashIcon s={14} />
                                </button>
                              )}
                            </div>
                          )}
                        </span>
                      </div>
                      );
                    })
                  )}
                </div>
              </section>
            )}

            {/* ═══════════════════════════════════════════════════
                Tab 3: Security
            ═══════════════════════════════════════════════════ */}
            {activeTab === 'Security' && (
              <section style={{ maxWidth: 520 }}>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>Security</h2>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 22 }}>Keep your account safe with a strong password.</div>

                {!canChangePassword ? (
                  <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--text-3)' }}>
                    <div style={{ fontSize: 28, marginBottom: 10 }}>🔒</div>
                    <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>Google sign-in account</div>
                    <div style={{ fontSize: 13 }}>Password changes are managed through your Google account.</div>
                  </div>
                ) : pwSuccess ? (
                  <div className="card" style={{ padding: 28, textAlign: 'center' }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(16,185,129,.15)', border: '1px solid rgba(16,185,129,.3)', display: 'grid', placeItems: 'center', margin: '0 auto 16px', fontSize: 22 }}>✓</div>
                    <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6 }}>Password changed!</div>
                    <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20 }}>Your password has been updated successfully.</div>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setPwSuccess(false); setPwStep(1); }}>Change again</button>
                  </div>
                ) : (
                  <div className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Step indicator */}
                    <div className="row" style={{ gap: 0, marginBottom: 4 }}>
                      {[1, 2].map(s => (
                        <div key={s} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                          <div style={{
                            width: 24, height: 24, borderRadius: '50%', display: 'grid', placeItems: 'center',
                            fontSize: 11, fontWeight: 700,
                            background: pwStep >= s ? 'rgba(255,77,141,.15)' : 'rgba(255,255,255,.04)',
                            border: `1px solid ${pwStep >= s ? 'rgba(255,77,141,.4)' : 'var(--border)'}`,
                            color: pwStep >= s ? '#FFC2B5' : 'var(--text-4)',
                          }}>{s}</div>
                          {s < 2 && <div style={{ width: 40, height: 1, background: pwStep > 1 ? 'rgba(255,77,141,.3)' : 'var(--border)' }} />}
                        </div>
                      ))}
                      <span style={{ fontSize: 12, color: 'var(--text-4)', marginLeft: 12 }}>
                        {pwStep === 1 ? 'Enter new password' : 'Verify by email code'}
                      </span>
                    </div>

                    {pwStep === 1 ? (
                      <>
                        <PwField label="Current password" value={oldPassword} onChange={setOldPassword} show={showOldPw} onToggleShow={() => setShowOldPw(v => !v)} placeholder="••••••••" />
                        <PwField label="New password" value={newPassword} onChange={setNewPassword} show={showNewPw} onToggleShow={() => setShowNewPw(v => !v)} placeholder="At least 8 characters" />
                        <FieldInput label="Confirm new password" type="password" value={confirmPassword} onChange={setConfirmPassword} placeholder="Repeat new password" />
                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                          <div style={{ fontSize: 12, color: 'var(--red)', marginTop: -8 }}>Passwords do not match</div>
                        )}
                        <button className="btn btn-primary" onClick={handleRequestOtp} disabled={otpSending} style={{ justifyContent: 'center' }}>
                          {otpSending ? 'Sending code…' : 'Send verification code'}
                        </button>
                      </>
                    ) : (
                      <>
                        <div style={{ padding: '10px 14px', borderRadius: 9, background: 'rgba(16,185,129,.06)', border: '1px solid rgba(16,185,129,.2)', fontSize: 13, color: '#10B981' }}>
                          A 6-digit code was sent to your email. Check your inbox.
                        </div>
                        <div>
                          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>Verification code</div>
                          <input
                            type="text" maxLength={6} value={otp}
                            onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="000000"
                            style={{
                              width: '100%', height: 50, padding: '0 12px', borderRadius: 9, textAlign: 'center',
                              fontSize: 24, fontWeight: 700, letterSpacing: '0.35em', fontFamily: 'monospace',
                              background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
                              color: '#fff', outline: 'none', boxSizing: 'border-box',
                            }}
                          />
                        </div>
                        <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setPwStep(1)}>Back</button>
                          <button className="btn btn-primary btn-sm" onClick={handleChangePassword} disabled={pwChanging}>
                            {pwChanging ? 'Changing…' : 'Change password'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Danger zone */}
                <div style={{ marginTop: 28 }}>
                  <h2 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--red)' }}>Danger zone</h2>
                  <div className="card" style={{ padding: '16px 18px', borderColor: 'rgba(245,87,108,.25)', background: 'rgba(245,87,108,.04)' }}>
                    <div className="row" style={{ justifyContent: 'space-between', gap: 18 }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600 }}>Sign out</div>
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>Sign out on this device.</div>
                      </div>
                      <button className="btn btn-sm" onClick={() => setLogoutOpen(true)} style={{ background: 'rgba(245,87,108,.1)', border: '1px solid rgba(245,87,108,.4)', color: 'var(--red)' }}>
                        Sign out
                      </button>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* ═══════════════════════════════════════════════════
                Tab 4: Team
            ═══════════════════════════════════════════════════ */}
            {activeTab === 'Team' && (
              <section>
                {/* Received invitations */}
                {invLoading ? (
                  <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <Skeleton h={60} />
                  </div>
                ) : invError ? (
                  <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: 'rgba(245,87,108,.06)', border: '1px solid rgba(245,87,108,.2)', fontSize: 13, color: 'var(--red)' }}>
                    {invError} <button className="btn btn-ghost btn-sm" onClick={fetchInvitations} style={{ marginLeft: 10 }}>Retry</button>
                  </div>
                ) : receivedInvitations.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 10 }}>Pending invitations</div>
                    {receivedInvitations.map(inv => (
                      <div key={inv.invitation_id} className="row acct-inv-row" style={{
                        gap: 14, padding: '14px 18px', borderRadius: 12, marginBottom: 10,
                        background: 'rgba(99,102,241,.06)', border: '1px solid rgba(99,102,241,.2)',
                      }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,#6366F1,#8B5CF6)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                          <UserIcon s={16} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{inv.inviter_name || inv.inviting_client_name || 'Unknown'}</div>
                          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{inv.inviter_email || ''} · Sub-account invitation</div>
                        </div>
                        <div className="row" style={{ gap: 8 }}>
                          <button
                            className="btn btn-ghost btn-sm"
                            disabled={!!invitationActioning}
                            onClick={() => handleRejectInvitation(inv)}
                          >
                            {invitationActioning === inv.invitation_id + '_reject' ? 'Declining…' : 'Decline'}
                          </button>
                          <button
                            className="btn btn-primary btn-sm"
                            disabled={!!invitationActioning}
                            onClick={() => handleAcceptInvitation(inv)}
                          >
                            <CheckIcon s={12} />
                            {invitationActioning === inv.invitation_id + '_accept' ? 'Accepting…' : 'Accept'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sub-accounts list */}
                {!isSubAccount && (
                  <>
                    <div className="row" style={{ justifyContent: 'space-between', marginBottom: 14 }}>
                      <div>
                        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>Team &amp; sub-accounts</h2>
                        <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>Sub-accounts share your balance with individual spending limits.</div>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/sub-accounts')}>
                        <PlusIcon s={12} /> Manage sub-accounts
                      </button>
                    </div>

                    <div className="card" style={{ overflow: 'hidden' }}>
                      {subLoading ? (
                        <div style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <Skeleton h={50} /><Skeleton h={50} />
                        </div>
                      ) : subAccounts.length === 0 ? (
                        <div style={{ padding: '32px 18px', textAlign: 'center', color: 'var(--text-4)', fontSize: 13 }}>
                          No sub-accounts yet.{' '}
                          <button onClick={() => navigate('/sub-accounts')} style={{ color: '#A5B4FC', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>Create one →</button>
                        </div>
                      ) : (
                        subAccounts.map((sa, i) => {
                          const saInitials = (sa.client_name || sa.name || '?').split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
                          const gradients = ['linear-gradient(135deg,#6366F1,#A78BFA)', 'linear-gradient(135deg,#FF7E67,#FF4D8D)', 'linear-gradient(135deg,#10B981,#6366F1)'];
                          return (
                            <div key={sa.client_id || sa.id || i} className="row" style={{ padding: '14px 18px', borderBottom: i < subAccounts.length - 1 ? '1px solid var(--border)' : 'none', gap: 14 }}>
                              <span style={{ width: 32, height: 32, borderRadius: '50%', background: gradients[i % 3], display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                                {saInitials}
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sa.client_name || sa.name || '—'}</div>
                                <div style={{ fontSize: 11, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sa.client_email || sa.email || ''}</div>
                              </div>
                              <span className="chip" style={{ height: 22, fontSize: 11 }}>
                                {sa.is_active ? 'Active' : 'Inactive'}
                              </span>
                              {sa.balance != null && (
                                <span style={{ fontSize: 12, color: 'var(--text-3)', minWidth: 60, textAlign: 'right' }}>
                                  {Number(sa.balance).toFixed(4)} URO
                                </span>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}

                {isSubAccount && receivedInvitations.length === 0 && !invLoading && (
                  <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-4)', fontSize: 14 }}>
                    No pending invitations
                  </div>
                )}
              </section>
            )}

            {/* ═══════════════════════════════════════════════════
                Tab 5: Usage
            ═══════════════════════════════════════════════════ */}
            {activeTab === 'Usage' && (
              <section>
                <h2 style={{ margin: '0 0 4px', fontSize: 18, fontWeight: 700, letterSpacing: '-0.02em' }}>Usage</h2>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginBottom: 22 }}>API usage over the last 30 days.</div>

                {usageLoading ? (
                  <div className="acct-usage-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    <Skeleton h={100} /><Skeleton h={100} /><Skeleton h={100} />
                  </div>
                ) : usageError ? (
                  <div className="acct-usage-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                    {[
                      { label: 'Total API calls', value: '—' },
                      { label: 'Input tokens', value: '—' },
                      { label: 'Total spent (30d)', value: '—' },
                    ].map(stat => (
                      <div key={stat.label} className="card" style={{ padding: 20 }}>
                        <div style={{ fontSize: 11, color: 'var(--text-4)', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>{stat.label}</div>
                        <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-3)' }}>{stat.value}</div>
                      </div>
                    ))}
                    <div className="row" style={{ gap: 10, marginTop: 4, gridColumn: '1/-1' }}>
                      <div style={{ fontSize: 13, color: 'var(--text-3)' }}>{usageError}</div>
                      <button className="btn btn-ghost btn-sm" onClick={fetchUsage}>Retry</button>
                    </div>
                  </div>
                ) : usage == null ? (
                  <>
                    <div className="acct-usage-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
                      {[
                        { label: 'Total API calls', value: '—', sub: 'Last 30 days' },
                        { label: 'Input tokens', value: '—', sub: 'Last 30 days' },
                        { label: 'Total spent', value: '—', sub: 'Last 30 days' },
                      ].map(stat => (
                        <div key={stat.label} className="card" style={{ padding: 20 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-4)', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>{stat.label}</div>
                          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-3)' }}>{stat.value}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{stat.sub}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-4)', padding: '12px 0' }}>Detailed analytics coming soon.</div>
                    <button onClick={() => navigate('/api-usage')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 16px', borderRadius: 9, background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.3)', color: '#A5B4FC', fontSize: 13, fontWeight: 600, cursor: 'pointer', marginTop: 8 }}>
                      View detailed API analytics →
                    </button>
                  </>
                ) : (
                  <>
                    <div className="acct-usage-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
                      {[
                        { label: 'Total API calls', value: usage.summary ? ((usage.summary.total_calls ?? 0) + (usage.summary.total_image_jobs ?? 0) + (usage.summary.total_workflow_runs ?? 0)).toLocaleString() : '—', sub: 'LLM + image + workflow · Last 30 days' },
                        { label: 'Input tokens', value: (usage.summary?.total_input_tokens ?? '—').toLocaleString?.() ?? '—', sub: 'Last 30 days' },
                        { label: 'Total spent', value: usage.summary?.total_cost != null ? `$${Number(usage.summary.total_cost).toFixed(2)}` : '—', sub: 'Last 30 days' },
                      ].map(stat => (
                        <div key={stat.label} className="card" style={{ padding: 20 }}>
                          <div style={{ fontSize: 11, color: 'var(--text-4)', letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 10 }}>{stat.label}</div>
                          <div style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-0.02em' }}>{stat.value}</div>
                          <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 4 }}>{stat.sub}</div>
                        </div>
                      ))}
                    </div>
                    <button onClick={() => navigate('/api-usage')} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, height: 36, padding: '0 16px', borderRadius: 9, background: 'rgba(99,102,241,.1)', border: '1px solid rgba(99,102,241,.3)', color: '#A5B4FC', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      View detailed API analytics →
                    </button>
                  </>
                )}
              </section>
            )}

          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════
          MODALS
      ═══════════════════════════════════════════════════════════ */}

      {/* New API key created */}
      <Modal open={showKeyModal} onClose={() => { setShowKeyModal(false); setCreatedKey(null); }} title="API Key created" disableLightDismiss>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)' }}>
            <code style={{ fontSize: 13, color: '#FFC2A8', wordBreak: 'break-all', display: 'block', fontFamily: 'monospace', lineHeight: 1.6 }}>{createdKey}</code>
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              className="btn btn-primary" onClick={handleCopyKey}
              style={{ flex: 1, justifyContent: 'center', gap: 8 }}
            >
              <CopyIcon s={13} /> {copied ? 'Copied!' : 'Copy key'}
            </button>
          </div>
          <div style={{ fontSize: 13, color: '#fbbf24', background: 'rgba(245,158,11,.08)', border: '1px solid rgba(245,158,11,.2)', borderRadius: 9, padding: '10px 14px' }}>
            ⚠ This key will only be shown once. Store it securely.
          </div>
          <button className="btn btn-ghost" onClick={() => { setShowKeyModal(false); setCreatedKey(null); }} style={{ justifyContent: 'center' }}>
            Done
          </button>
        </div>
      </Modal>

      {/* Create / edit API key */}
      <Modal
        open={!!keyForm}
        onClose={closeKeyForm}
        closeDisabled={keyFormSaving}
        returnFocusRef={apiKeysTabRef}
        title={
          keyForm?.mode === 'edit' ? 'Edit API key'
            : keyForm?.mode === 'connector' ? (connectorContext ? (connectorContext.verified ? `Connect ${connectorContext.host}` : 'Connect an unverified application') : 'Connect your assistant')
            : 'Create API key'
        }
        width={440}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Arrived from a connector: paste a key you have, or make one.
              Paste comes first because it is the one that was missing. */}
          {keyForm?.mode === 'connector' && (
            <>
              <div style={{ padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,.04)', border: '1px solid var(--border)', fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                {connectorLoading && <div role="status">Checking connection details…</div>}
                {connectorContext && <>
                  <strong>{connectorContext.verified ? connectorContext.host : 'Unverified application'}</strong>
                  {!connectorContext.verified && <div>Declared name: {connectorContext.name}</div>}
                  {connectorContext.redirectOrigin === null
                    ? <div>App callback: <strong>{connectorContext.redirectScheme}</strong></div>
                    : <div>Your browser will return to: <strong>{connectorContext.redirectOrigin}</strong></div>}
                  <div style={{ marginTop: 8 }}>Only continue if you started this connection and recognize this application.</div>
                </>}
                {connectorError && <div role="alert">{connectorError}</div>}
                {connectorError && <button className="btn btn-ghost btn-sm" disabled={connectorLoading || keyFormSaving} onClick={() => loadConnectorContext(true)}>Retry connection details</button>}
              </div>
              <div onKeyDown={e => e.key === 'Enter' && connectWithPastedKey()}>
                <FieldInput
                  label="Paste a key you already have"
                  value={keyFormPaste}
                  onChange={setKeyFormPaste}
                  placeholder="uro_…"
                  type="password"
                  disabled={keyFormSaving}
                />
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={connectWithPastedKey}
                disabled={keyFormSaving || !connectorReady || !keyFormPaste.trim()}
              >
                Connect with this key
              </button>
              <div className="row" style={{ alignItems: 'center', gap: 10, color: 'var(--text-4)', fontSize: 11 }}>
                <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.1)' }} />
                or make a new one
                <span style={{ flex: 1, height: 1, background: 'rgba(255,255,255,.1)' }} />
              </div>
            </>
          )}
          {keyForm?.mode === 'edit' && (
            <div className="mono" style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text-3)' }}>
              {keyForm.key.api_key_prefix || keyForm.key.key_preview || ''}
            </div>
          )}
          {keyFormStatus && <div role="status" style={{ fontSize: 13, color: 'var(--text-3)', lineHeight: 1.5 }}>{keyFormStatus}</div>}
          <div onKeyDown={e => e.key === 'Enter' && !keyFormSaving && handleKeyFormSubmit()}>
            <FieldInput label="Name" value={keyFormName} onChange={setKeyFormName} placeholder="e.g. Production backend" maxLength={255} disabled={keyFormSaving || keyFormCreated} />
          </div>
          <div>
            <label htmlFor={keyDescriptionId} style={{ display: 'block', fontSize: 10, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 6 }}>Description</label>
            <textarea
              id={keyDescriptionId}
              value={keyFormDescription}
              disabled={keyFormSaving || keyFormCreated}
              onChange={e => setKeyFormDescription(e.target.value)}
              placeholder="What is this key used for? (optional)"
              maxLength={255}
              style={{
                width: '100%', minHeight: 68, padding: '9px 12px', borderRadius: 9,
                background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.1)',
                color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box',
                fontFamily: 'inherit', resize: 'vertical',
              }}
            />
            {keyFormDescription.length >= 200 && (
              <div style={{ fontSize: 10, color: 'var(--text-4)', textAlign: 'right', marginTop: 4 }}>
                {keyFormDescription.length}/255
              </div>
            )}
          </div>
          <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={closeKeyForm} disabled={keyFormSaving}>Cancel</button>
            <button
              className={keyForm?.mode === 'connector' ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}
              onClick={handleKeyFormSubmit}
              disabled={keyFormSaving || keyFormCreated || !canManageKeys || !keyFormName.trim() || (keyForm?.mode === 'connector' && !connectorReady)}
            >
              {keyForm?.mode === 'edit'
                ? (keyFormSaving ? 'Saving…' : 'Save changes')
                : (keyFormSaving ? (keyFormStatus === 'Connecting…' ? 'Connecting…' : 'Creating…') : 'Create key')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Disable / enable confirm. Deliberately NOT the inline Yes/No used by
          delete — two different actions collapsing into the same red confirm in
          the same 92px box is a mis-click generator. */}
      <Modal
        open={!!powerForm}
        onClose={() => !togglingKeyId && setPowerForm(null)}
        title={powerForm?.next ? 'Enable API key' : 'Disable API key'}
        width={440}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {powerForm?.key?.api_key_prefix || powerForm?.key?.key_preview || '•••'}
            </div>
            <div style={{ fontSize: 13, marginTop: 2 }}>{powerForm?.key?.api_key_name || '—'}</div>
          </div>

          <div style={{
            padding: '10px 14px', borderRadius: 9, fontSize: 12, lineHeight: 1.5,
            background: powerForm?.next ? 'rgba(16,185,129,.08)' : 'rgba(245,158,11,.08)',
            border: `1px solid ${powerForm?.next ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.2)'}`,
            color: powerForm?.next ? '#6EE7B7' : '#FBBF24',
          }}>
            {powerForm?.next
              ? (powerForm?.key?.disabled_at
                  ? `${powerForm.key.disabled_by_is_self ? 'You' : (powerForm.key.disabled_by_name || 'Someone')} disabled this key ${eventTime(powerForm.key.disabled_at)}. Enabling it will let requests through again.`
                  : 'This key was disabled outside the dashboard. Enabling it will let requests through again.')
              : 'Requests using this key will start failing immediately. You can enable it again at any time.'}
          </div>

          <FieldInput
            label="Reason (optional)"
            value={powerReason}
            onChange={setPowerReason}
            placeholder={powerForm?.next ? 'e.g. Verified — safe to re-enable' : 'e.g. Rotating credentials'}
            maxLength={255}
          />

          <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setPowerForm(null)} disabled={!!togglingKeyId}>Cancel</button>
            <button
              className="btn btn-primary btn-sm"
              disabled={!!togglingKeyId}
              onClick={() => applyKeyPower(powerForm.key, powerForm.next, powerReason.trim())}
            >
              {togglingKeyId
                ? (powerForm?.next ? 'Enabling…' : 'Disabling…')
                : (powerForm?.next ? 'Enable key' : 'Disable key')}
            </button>
          </div>
        </div>
      </Modal>

      {/* Key history — a modal, not an expandable row: the row is a flex
          container with hand-written mobile ordering, and this needs its own
          scroll container. */}
      <Modal open={!!historyKey} onClose={() => setHistoryKey(null)} title="Key history" width={520}>
        <div>
          <div className="row" style={{ gap: 8, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid var(--border)' }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {historyKey?.api_key_prefix || historyKey?.key_preview || '•••'}
            </span>
            <span style={{ color: 'var(--text-4)' }}>·</span>
            <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {historyKey?.api_key_name || '—'}
            </span>
          </div>

          {historyLoading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[1, 2, 3].map(i => <Skeleton key={i} h={44} />)}
            </div>
          ) : historyError ? (
            <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--text-3)' }}>
              <div style={{ fontSize: 14, marginBottom: 10 }}>{historyError}</div>
              <button className="btn btn-ghost btn-sm" onClick={() => openKeyHistory(historyKey)}>Retry</button>
            </div>
          ) : (
            <div className="scroll-y" style={{ maxHeight: 340, overflowY: 'auto' }}>
              {historyEvents.length === 0 && (
                <div style={{ textAlign: 'center', padding: '18px 0 6px' }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-2)' }}>No changes yet</div>
                  <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 4 }}>
                    This key has not been disabled or enabled since it was created.
                  </div>
                </div>
              )}
              {historyEvents.map((ev) => {
                const off = ev.action === 'disabled';
                const evTone = off ? '#F59E0B' : '#10B981';
                const who = ev.actor_is_self ? 'you' : (ev.actor_name || 'Someone');
                const auth = ACTOR_AUTH_LABEL[ev.actor_auth_type];
                return (
                  <div key={ev.event_id} style={{ padding: '11px 0', borderTop: '1px solid var(--border)' }}>
                    <div className="row" style={{ gap: 9, alignItems: 'flex-start' }}>
                      <span style={{ color: evTone, marginTop: 2, flexShrink: 0 }}><PowerIcon s={13} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>
                          <span style={{ color: evTone }}>{off ? 'Disabled' : 'Enabled'}</span>
                          <span style={{ color: 'var(--text-2)', fontWeight: 500 }}> by {who}</span>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-4)', marginTop: 2 }}>
                          {ACTOR_ROLE_LABEL[ev.actor_role] || 'Unknown'}{auth ? ` · ${auth}` : ''}
                        </div>
                        {ev.reason ? (
                          <div style={{
                            fontSize: 12, color: 'var(--text-2)', marginTop: 7, padding: '7px 10px',
                            borderRadius: 8, background: 'rgba(255,255,255,.04)',
                            border: '1px solid var(--border)', wordBreak: 'break-word', lineHeight: 1.45,
                          }}>
                            “{ev.reason}”
                          </div>
                        ) : null}
                      </div>
                      <span title={eventTimeFull(ev.created_at)} style={{ fontSize: 11, color: 'var(--text-4)', flexShrink: 0, whiteSpace: 'nowrap', marginTop: 2 }}>
                        {eventTime(ev.created_at)}
                      </span>
                    </div>
                  </div>
                );
              })}
              {/* Never present a partial log as complete. */}
              {historyTruncated && (
                <div style={{ fontSize: 11, color: 'var(--text-4)', textAlign: 'center', padding: '8px 0', borderTop: '1px solid var(--border)' }}>
                  Showing the most recent {historyEvents.length} changes. Older entries are not shown.
                </div>
              )}
              {/* Anchor: costs nothing (created_at is already on the row) and
                  turns the empty state into a real screen. Hidden when the log
                  is truncated — sitting it directly under the oldest shown entry
                  would imply nothing happened in between. */}
              {!historyTruncated && (
                <div className="row" style={{ gap: 9, padding: '11px 0', borderTop: '1px solid var(--border)' }}>
                  <span style={{ color: 'var(--text-4)', flexShrink: 0 }}><HistoryIcon s={13} /></span>
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--text-3)' }}>Key created</span>
                  <span title={eventTimeFull(historyKey?.created_at)} style={{ fontSize: 11, color: 'var(--text-4)' }}>
                    {eventTime(historyKey?.created_at)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="row" style={{ gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setHistoryKey(null)}>Close</button>
          </div>
        </div>
      </Modal>

      {/* Edit profile */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)} title="Edit profile">
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleProfileImageChange} style={{ display: 'none' }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              {editImagePreview
                ? <img src={editImagePreview} alt="" style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,.12)' }} />
                : <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg,#FF7E67,#FF4D8D)', display: 'grid', placeItems: 'center', fontSize: 30, fontWeight: 700 }}>{initials}</div>
              }
              <button
                onClick={() => fileInputRef.current?.click()}
                aria-label="Change profile photo"
                style={{ position: 'absolute', bottom: 2, right: 2, width: 26, height: 26, borderRadius: '50%', background: 'rgba(255,255,255,.15)', border: '1px solid rgba(255,255,255,.25)', color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: 12 }}
              >✎</button>
            </div>
            {imageUploading && <div style={{ fontSize: 12, color: 'var(--text-4)', marginTop: 8 }}>Uploading…</div>}
          </div>
          <FieldInput label="Display name" value={editName} onChange={setEditName} placeholder="Your name" />
          <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditModalOpen(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={handleSaveProfile} disabled={profileUpdating || imageUploading}>
              {profileUpdating ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Logout confirm */}
      <Modal open={logoutOpen} onClose={() => setLogoutOpen(false)} title="Sign out" width={380}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ fontSize: 14, color: 'var(--text-3)' }}>Are you sure you want to sign out?</div>
          <div className="row" style={{ gap: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setLogoutOpen(false)}>Cancel</button>
            <button className="btn btn-sm" onClick={handleLogout} style={{ background: 'rgba(245,87,108,.12)', border: '1px solid rgba(245,87,108,.4)', color: 'var(--red)' }}>
              Sign out
            </button>
          </div>
        </div>
      </Modal>

    </AppScreenWrapper>
  );
}

export default AccountScreen;
